import Footer from '../../Footer/Footer';
import { Link, useHistory, useParams } from 'react-router-dom';
import { frontendRoutes } from '../../../Constants/constants';
import { convertDistance, getDistance } from 'geolib';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import { Button } from '../../Button/Button';
import { getBuilderId } from '../../../redux/action/action';
import { ReactComponent as BackIcon } from '../../../assets/images/icn-arrow-prev.svg';

const LOCATION_TIMEOUT_MS = 15000;
type LocationError = 'timeout' | 'permission_denied' | 'unavailable' | 'unsupported' | 'unexpected';

const locationErrorMessages: Record<LocationError, string> = {
  timeout: "We couldn't get your location in time. Please try again.",
  permission_denied: 'Location access was denied. Enable location permission for this site in your browser settings, then retry.',
  unavailable: 'Your location is unavailable. Check your device location services and connection, then retry.',
  unsupported: 'Your browser does not support location sharing. Please use a browser that supports location services.',
  unexpected: "We couldn't get your location. Please try again.",
};

const ShareLocation = () => {
  const [locationError, setLocationError] = useState<LocationError | null>(null);
  const [isLocating, setIsLocating] = useState(false);
  const requestIdRef = useRef(0);
  const pendingRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestIdRef.current += 1;
      pendingRef.current = false;
      if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);
    };
  }, []);
  const [distance, setDistance] = useState(0);

  const dispatch = useDispatch();
  const history = useHistory();
  const params = useParams<{ builder_name?: string }>();
  const property = useSelector((state: any) => state.property.property);
  const propertyId = useSelector((state: any) => state.property.property?.id);
  const builderId = useSelector((state: any) => state.builder.builder_id);

  useEffect(() => {
    if (!builderId) {
      dispatch(getBuilderId());
    }
  }, [dispatch, builderId]);

  const handleGetPosition = ({ coords }: { coords: { latitude: number; longitude: number } }) => {
    const currentPosition = {
      latitude: coords.latitude,
      longitude: coords.longitude,
    };
    const propertyPosition = {
      latitude: property.latitude,
      longitude: property.longitude,
    };
    const distance = getDistance(currentPosition, propertyPosition);
    const distanceInMiles = convertDistance(distance, 'mi');

    if (distanceInMiles > 8) {
      setDistance(distanceInMiles);
      return;
    }

    history.push(frontendRoutes.LockCodeScreen(params.builder_name).path);
  };

  const onAccept = (event: React.SyntheticEvent) => {
    event.preventDefault();
    if (pendingRef.current) return;
    setLocationError(null);

    if (!navigator.geolocation || typeof navigator.geolocation.getCurrentPosition !== 'function') {
      setLocationError('unsupported');
      return;
    }

    pendingRef.current = true;
    setIsLocating(true);
    const requestId = ++requestIdRef.current;

    const finish = (error?: LocationError) => {
      if (!mountedRef.current || requestId !== requestIdRef.current) return false;
      requestIdRef.current += 1;
      pendingRef.current = false;
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setIsLocating(false);
      if (error) setLocationError(error);
      return true;
    };

    timeoutRef.current = setTimeout(() => finish('timeout'), LOCATION_TIMEOUT_MS);
    try {
      navigator.geolocation.getCurrentPosition(
        position => {
          if (finish()) handleGetPosition(position);
        },
        error => {
          let type: LocationError = 'unexpected';

          switch (error.code) {
            case 1:
              type = 'permission_denied';
              break;
            case 2:
              type = 'unavailable';
              break;
            case 3:
              type = 'timeout';
              break;
            default:
              break;
          }

          finish(type);
        },
        { enableHighAccuracy: true, timeout: LOCATION_TIMEOUT_MS },
      );
    } catch (_error) {
      finish('unexpected');
    }
  };

  let locationButtonLabel = 'Continue';
  if (isLocating) {
    locationButtonLabel = 'Getting location...';
  } else if (locationError) {
    locationButtonLabel = 'Retry';
  }


  if (distance > 8) {
    return (
      <section className="main-section d-flex justify-content-center">
        <div className="box">
          <div className="status-title error">
            <h1>Uh-Oh</h1>
            <span>It appears that you have not yet arrived at your destination.</span>
            <div>
              <span onClick={() => history.goBack()} className="a">
                Back
              </span>
              <Link to={frontendRoutes.selectTourDate(params.builder_name, propertyId).path}>
                Reschedule
              </Link>
              <Link to={frontendRoutes.chooseCommunity(params.builder_name).path}>Start Over</Link>
            </div>
          </div>
          <h2 className="title">You’re just a short {Math.ceil(distance)} miles away!</h2>
          <div className="text-container">
            <p className="text">
              Please return when you arrive on-site to continue your self-tour. For help, call
              NterNow support at{' '}
              <a href="tel:+1-844-531-1283">(844) 531-1283</a>.
            </p>
          </div>
          <a href="tel:+1-844-531-1283" className="need-help-link" target="_blank" rel="noreferrer">
            Need Help? <i className="icn icn-phone"></i>
          </a>
          <div style={{ padding: '96px 40px 60px 40px' }}>
            <Button
              onClick={() => {
                history.push(frontendRoutes.selectTourDate(params.builder_name, propertyId).path);
              }}
              className="button back-button mb-5"
            >
              <BackIcon />
              Back
            </Button>
            <p className="text">
              Need help with your self-tour? Call NterNow support at{' '}
              <a href="tel:+1-844-531-1283">(844) 531-1283</a>.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="main-section location-screen d-flex justify-content-center">
      <div className="box">
        <div className="main-title"></div>
        <span onClick={() => history.goBack()} className="link-back icn icn-back-arrow"></span>

        <div className="box-data d-flex flex-column text-center">
          <i className={`icn ${locationError ? 'icn-error-pin' : 'icn-pin'}`}></i>
          {locationError ? (
            <>
              <h1 className="h2">Uh-Oh</h1>
              <div className="error-message-container">
                <p className='m-0' role="alert">
                  {locationErrorMessages[locationError]} To continue your self-tour, we need to verify
                  that you are in front of the property. Otherwise, you may{' '}
                  <Link to={frontendRoutes.selectTourDate(params.builder_name, propertyId).path}>
                    reschedule
                  </Link>{' '}
                  or{' '}
                  <Link to={frontendRoutes.chooseCommunity(params.builder_name).path}>
                    start over
                  </Link>
                </p>
              </div>

              <div className='troubleshoot-points'>
                <h4 className='text-danger mt-4 mb-3'>Troubleshooting Steps</h4>
                <ol>
                  <li>Make sure to turn on location sharing in your phone's browser. Here's a helpful resource: <a href="https://nternow.freshdesk.com/en/support/solutions/articles/63000280201-how-to-enable-location-services" rel="noreferrer"  target='_blank'>link</a></li>
                  <li>Make sure you are near or close to the property to TourNow</li>
                  <li>Call live support if in proper location and still receiving error</li>
                </ol>
                <a href="tel:+1-844-531-1283" className="need-help-link d-flex justify-content-center gap-1 mt-3" target="_blank" rel="noreferrer">
                  Need Help? <i className="icn icn-phone"></i>
                </a>
              </div>
            </>
          ) : (
            <>
              <h1 className="h2">{isLocating ? 'Getting your location...' : 'Please share your location'}</h1>
              <p>
                To continue your self-tour, we will need you to agree to share your
                location to verify you are in front of the property.
              </p>
            </>
          )}
          <div className="box-footer mt-auto">
            {isLocating && <p role="status" aria-live="polite">Please wait while we verify your location...</p>}
            <Button onClick={onAccept} disabled={isLocating} className="button w-100">
              {locationButtonLabel}
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    </section>
  );
};

export default ShareLocation;
